import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import { Alert, ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  now: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1, 3),
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    marginBottom: theme.spacing(2),
  },
  nowBig: { fontWeight: 700, fontSize: "1.05rem" },
  open: { color: theme.palette.success.main },
  closed: { color: theme.palette.error.main },
  spacer: { flex: 1 },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  days: { borderRadius: 10, overflow: "hidden" },
  day: {
    display: "grid",
    gridTemplateColumns: "130px auto minmax(0, 1fr)",
    gap: theme.spacing(1, 2),
    alignItems: "center",
    padding: theme.spacing(1.25, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    "&:first-child": { borderTop: 0 },
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  dayName: { fontWeight: 600 },
  daySub: { color: theme.palette.text.secondary, fontSize: "0.75rem" },
  controls: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1), alignItems: "center" },
  time: { width: 122 },
  track: {
    position: "relative",
    height: 10,
    borderRadius: 99,
    backgroundColor: theme.palette.action.disabledBackground,
    overflow: "hidden",
    minWidth: 120,
  },
  fill: {
    position: "absolute",
    top: 0,
    bottom: 0,
    borderRadius: 99,
    backgroundColor: theme.palette.primary.main,
  },
  fillAll: { backgroundColor: theme.palette.success.main },
  axis: {
    display: "flex",
    justifyContent: "space-between",
    color: theme.palette.text.secondary,
    fontSize: "0.65rem",
    marginTop: 2,
  },
  saveBar: {
    position: "sticky",
    bottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 2),
    borderRadius: 10,
    zIndex: 2,
  },
  hint: { color: theme.palette.text.secondary, fontSize: "0.8rem", marginTop: theme.spacing(1.5) },
}));

const WEEKDAYS = [
  ["Segunda-feira", "monday"],
  ["Terça-feira", "tuesday"],
  ["Quarta-feira", "wednesday"],
  ["Quinta-feira", "thursday"],
  ["Sexta-feira", "friday"],
  ["Sábado", "saturday"],
  ["Domingo", "sunday"],
];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Mesma regra do formulário antigo: dia em branco = sem restrição (aberto 24h);
// janela vazia ou invertida nunca abre, então é mostrada como fechado.
const modeOf = (schedule) => {
  if (schedule.closed === true) return "closed";
  if (schedule.startTime && schedule.endTime) {
    const valid = TIME_RE.test(schedule.startTime) && TIME_RE.test(schedule.endTime);
    if (valid && schedule.startTime >= schedule.endTime) return "closed";
    return "hours";
  }
  return "open";
};

const toRows = (list) =>
  WEEKDAYS.map(([weekday, weekdayEn]) => {
    const found = (Array.isArray(list) ? list : []).find((s) => s.weekdayEn === weekdayEn) || {};
    return {
      weekday,
      weekdayEn,
      mode: modeOf(found),
      startTime: found.startTime || "",
      endTime: found.endTime || "",
    };
  });

const toPayload = (rows) =>
  rows.map(({ mode, weekday, weekdayEn, startTime, endTime }) =>
    mode === "hours"
      ? { weekday, weekdayEn, startTime, endTime, closed: false }
      : { weekday, weekdayEn, startTime: "", endTime: "", closed: mode === "closed" }
  );

const rowError = (row) => {
  if (row.mode !== "hours") return "";
  if (!TIME_RE.test(row.startTime) || !TIME_RE.test(row.endTime)) return "Informe início e fim.";
  if (row.startTime >= row.endTime) return "O fim deve ser depois do início.";
  return "";
};

const toMinutes = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

const hoursLabel = (row) => {
  const h = (toMinutes(row.endTime) - toMinutes(row.startTime)) / 60;
  return `${String(Math.round(h * 10) / 10).replace(".", ",")} h de atendimento`;
};

const PRESETS = [
  { id: "biz", label: "Seg a sex, 08:00–18:00", days: 5 },
  { id: "sat", label: "Seg a sáb, 08:00–18:00", days: 6 },
  { id: "all", label: "Aberto 24h todos os dias", days: 0 },
];

const applyPreset = (rows, preset) =>
  rows.map((row, i) => {
    if (preset.id === "all") return { ...row, mode: "open", startTime: "", endTime: "" };
    return i < preset.days
      ? { ...row, mode: "hours", startTime: "08:00", endTime: "18:00" }
      : { ...row, mode: "closed", startTime: "", endTime: "" };
  });

// Dia da semana e hora em Brasília, que é o fuso que o backend usa.
const brasiliaNow = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Sao_Paulo",
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return {
    weekdayEn: get("weekday").toLowerCase(),
    time: `${get("hour").replace("24", "00")}:${get("minute")}`,
  };
};

function NowStatus({ rows, classes }) {
  const [now, setNow] = useState(brasiliaNow());
  useEffect(() => {
    const id = setInterval(() => setNow(brasiliaNow()), 30000);
    return () => clearInterval(id);
  }, []);

  const today = rows.find((r) => r.weekdayEn === now.weekdayEn);
  let text = null;
  if (!today || today.mode === "open") {
    text = <span className={classes.open}>Aberto · atendimento 24h hoje</span>;
  } else if (today.mode === "closed") {
    text = <span className={classes.closed}>Fora do expediente · fechado hoje</span>;
  } else if (now.time >= today.startTime && now.time <= today.endTime) {
    text = <span className={classes.open}>Aberto · até {today.endTime}</span>;
  } else {
    text = (
      <span className={classes.closed}>
        Fora do expediente · hoje atende das {today.startTime} às {today.endTime}
      </span>
    );
  }
  return (
    <div>
      <Typography variant="caption" color="textSecondary">Agora (horário de Brasília)</Typography>
      <Typography className={classes.nowBig}>{text}</Typography>
    </div>
  );
}

export default function CompanySchedules({
  initialValues,
  onSubmit,
  loading,
  companyMode,
  onGoToOptions,
  connectionsWithoutMessage = [],
}) {
  const classes = useStyles();
  const saved = useMemo(() => toRows(initialValues), [initialValues]);
  const [rows, setRows] = useState(saved);

  useEffect(() => {
    setRows(saved);
  }, [saved]);

  const dirty = JSON.stringify(rows) !== JSON.stringify(saved);
  const hasError = rows.some((r) => rowError(r));

  const patch = (index, change) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...change } : r)));

  const setMode = (index, mode) => {
    if (!mode) return;
    const row = rows[index];
    if (mode === "hours") {
      patch(index, {
        mode,
        startTime: row.startTime || "08:00",
        endTime: row.endTime || "18:00",
      });
    } else {
      patch(index, { mode, startTime: "", endTime: "" });
    }
  };

  const copyMonday = (count) =>
    setRows((prev) =>
      prev.map((r, i) =>
        i > 0 && i < count
          ? { ...r, mode: prev[0].mode, startTime: prev[0].startTime, endTime: prev[0].endTime }
          : r
      )
    );

  return (
    <div>
      <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
        Horário de atendimento
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Fora do horário, o cliente recebe a mensagem de fora do expediente da conexão. Vale o horário de Brasília.
      </Typography>

      <Paper variant="outlined" className={classes.now}>
        <NowStatus rows={saved} classes={classes} />
        <span className={classes.spacer} />
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="body2" color="textSecondary">Controle de expediente:</Typography>
          <Chip
            size="small"
            color={companyMode ? "primary" : "default"}
            label={companyMode ? "Por empresa" : "Não é por empresa"}
          />
          <Button size="small" variant="outlined" onClick={onGoToOptions}>Alterar em Opções</Button>
        </Box>
      </Paper>

      {!companyMode && (
        <Alert severity="info" style={{ marginBottom: 16 }}>
          Estes horários só valem quando o controle de expediente está em "Por empresa". Hoje o atendimento não segue estes horários.
        </Alert>
      )}

      {connectionsWithoutMessage.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          Sem a mensagem de fora do expediente, os horários não têm efeito e o atendimento segue normal. Falta nas conexões:{" "}
          <strong>{connectionsWithoutMessage.join(", ")}</strong>. Preencha em Conexões.
        </Alert>
      )}

      <div className={classes.toolbar}>
        <Typography variant="body2" color="textSecondary">Modelos prontos:</Typography>
        {PRESETS.map((preset) => (
          <Button key={preset.id} size="small" variant="outlined" onClick={() => setRows((prev) => applyPreset(prev, preset))}>
            {preset.label}
          </Button>
        ))}
        <span className={classes.spacer} />
        <Typography variant="body2" color="textSecondary">Copiar segunda para:</Typography>
        <Button size="small" variant="outlined" color="primary" onClick={() => copyMonday(5)}>Dias úteis</Button>
        <Button size="small" variant="outlined" color="primary" onClick={() => copyMonday(7)}>Todos os dias</Button>
      </div>

      <Paper variant="outlined" className={classes.days}>
        {rows.map((row, index) => {
          const error = rowError(row);
          const showBar = row.mode === "open" || (row.mode === "hours" && !error);
          const left = row.mode === "hours" && !error ? (toMinutes(row.startTime) / 1440) * 100 : 0;
          const width =
            row.mode === "hours" && !error
              ? ((toMinutes(row.endTime) - toMinutes(row.startTime)) / 1440) * 100
              : 100;
          const sub =
            row.mode === "closed" ? "Fechado o dia todo" : row.mode === "open" ? "Atende 24h" : error ? "" : hoursLabel(row);
          return (
            <div key={row.weekdayEn} className={classes.day}>
              <div>
                <Typography className={classes.dayName}>{row.weekday}</Typography>
                <Typography className={classes.daySub}>{sub}</Typography>
              </div>
              <div className={classes.controls}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={row.mode}
                  onChange={(_, mode) => setMode(index, mode)}
                  aria-label={`Funcionamento de ${row.weekday}`}
                >
                  <ToggleButton value="hours">Horário</ToggleButton>
                  <ToggleButton value="closed">Fechado</ToggleButton>
                  <ToggleButton value="open">24h</ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  type="time"
                  size="small"
                  variant="outlined"
                  label="Início"
                  className={classes.time}
                  InputLabelProps={{ shrink: true }}
                  value={row.startTime}
                  disabled={row.mode !== "hours"}
                  error={Boolean(error)}
                  onChange={(e) => patch(index, { startTime: e.target.value })}
                />
                <TextField
                  type="time"
                  size="small"
                  variant="outlined"
                  label="Fim"
                  className={classes.time}
                  InputLabelProps={{ shrink: true }}
                  value={row.endTime}
                  disabled={row.mode !== "hours"}
                  error={Boolean(error)}
                  onChange={(e) => patch(index, { endTime: e.target.value })}
                />
                {error && (
                  <Typography variant="caption" color="error" style={{ width: "100%" }}>
                    {error}
                  </Typography>
                )}
              </div>
              <div>
                <div className={classes.track}>
                  {showBar && (
                    <span
                      className={`${classes.fill} ${row.mode === "open" ? classes.fillAll : ""}`}
                      style={{ left: `${left}%`, width: `${width}%` }}
                    />
                  )}
                </div>
                <div className={classes.axis}>
                  <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>24h</span>
                </div>
              </div>
            </div>
          );
        })}
      </Paper>

      <Typography className={classes.hint}>
        <strong>Horário:</strong> atende dentro da janela e manda a mensagem fora dela. <strong>Fechado:</strong> manda a
        mensagem o dia todo. <strong>24h:</strong> atendimento normal, sem mensagem de fora do expediente.
      </Typography>

      <Paper variant="outlined" className={classes.saveBar}>
        <Typography variant="body2" color={hasError ? "error" : "textSecondary"} style={{ flex: 1 }}>
          {hasError
            ? "Corrija os horários marcados para salvar."
            : dirty
            ? "Há alterações não salvas."
            : "Nenhuma alteração."}
        </Typography>
        <Button disabled={!dirty || loading} onClick={() => setRows(saved)}>Descartar</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!dirty || hasError || loading}
          onClick={() => onSubmit(toPayload(rows))}
        >
          Salvar horários
        </Button>
      </Paper>
    </div>
  );
}
